class CourseInformationValidation extends HTMLElement {
  constructor() {
    super();
    this.form = document.querySelector('form[data-type="add-to-cart-form"]');
    this.hasLocationField =
      document.querySelector('[name="properties[Region]"]') !== null;
    this.hasCourseFields =
      document.querySelector('[name="properties[First Name]"]') !== null;
    this.courseContainers = document.querySelectorAll(".course-info-container");
    this.addToCartButton = document.querySelector(".product-form__submit");
    this.isFormExpanded = false;

    // Only initialize if we have form fields to show/hide
    if (this.form && (this.hasLocationField || this.hasCourseFields)) {
      // Initial state is collapsed
      this.collapseForm();

      // Override the submit button to show fields first
      if (this.addToCartButton) {
        this.addToCartButton.addEventListener(
          "click",
          this.handleButtonClick.bind(this)
        );
      }

      // Add an event listener for the Enter key on form fields
      this.form.addEventListener("keypress", (event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          this.validateForm(event);
        }
      });
    }
  }

  handleButtonClick(event) {
    event.preventDefault();

    if (!this.isFormExpanded) {
      // First click: expand the form, don't submit
      this.expandForm();
    } else {
      // Form is already expanded, validate before submitting
      this.validateForm(event);
    }
  }

  expandForm() {
    // Show the form containers with animation
    this.courseContainers.forEach((container) => {
      container.classList.add("active");
    });

    // Mark button as expanded
    if (this.addToCartButton) {
      this.addToCartButton.classList.add("expanded");
      this.addToCartButton.innerHTML = "<span>Continue to Checkout</span>";
    }

    this.isFormExpanded = true;

    // Focus on the first input
    setTimeout(() => {
      // Focus on region if it exists, otherwise first name
      const firstInput = this.hasLocationField
        ? document.getElementById("Region")
        : document.getElementById("FirstName");

      if (firstInput) firstInput.focus();
    }, 500); // After animation completes
  }

  collapseForm() {
    // Hide the form containers
    this.courseContainers.forEach((container) => {
      container.classList.remove("active");
    });

    // Reset button state
    if (this.addToCartButton) {
      this.addToCartButton.classList.remove("expanded");
    }

    this.isFormExpanded = false;
  }

  async validateForm(event) {
    event.preventDefault();

    // Reset error states
    this.resetErrors();

    // Check if all fields have values
    let isValid = true;

    // Only validate if we have fields
    if (this.hasLocationField || this.hasCourseFields) {
      // Validate region if it exists
      const regionField = document.getElementById("Region");
      if (regionField && regionField.value === "") {
        this.showError(regionField, "Please select your region");
        isValid = false;
      }

      // Only validate course fields if they exist
      if (this.hasCourseFields) {
        const firstNameField = document.getElementById("FirstName");
        const lastNameField = document.getElementById("LastName");
        const emailField = document.getElementById("Email");
        const phoneField = document.getElementById("Phone");

        if (firstNameField && !firstNameField.value.trim()) {
          this.showError(firstNameField, "Please enter a first name");
          isValid = false;
        }

        if (lastNameField && !lastNameField.value.trim()) {
          this.showError(lastNameField, "Please enter a last name");
          isValid = false;
        }

        if (emailField) {
          if (!emailField.value.trim()) {
            this.showError(emailField, "Please enter an email address");
            isValid = false;
          } else if (!this.isValidEmail(emailField.value.trim())) {
            this.showError(emailField, "Please enter a valid email address");
            isValid = false;
          } else {
            // Check for duplicate email in cart
            const isDuplicateEmail = await this.checkDuplicateEmailInCart(emailField.value.trim());
            if (isDuplicateEmail) {
              this.showError(
                emailField,
                "This email is already used for another course in your cart. Please use a unique email for each course."
              );
              isValid = false;
            }
          }
        }

        if (phoneField && phoneField.value.trim()) {
          // Only validate phone if it's not empty
          if (!this.isValidPhone(phoneField.value.trim())) {
            this.showError(
              phoneField,
              "Please enter a valid phone number (numbers only)"
            );
            isValid = false;
          }
        }
      }
    }

    // If all validations pass, submit the form
    if (isValid) {
      try {
        // Show loading state
        if (this.addToCartButton) {
          this.addToCartButton.classList.add("loading");
          this.addToCartButton.disabled = true;
        }

        // Submit the form programmatically
        this.form.submit();
      } catch (error) {
        console.error("Error submitting form:", error);

        // Reset button state on error
        if (this.addToCartButton) {
          this.addToCartButton.classList.remove("loading");
          this.addToCartButton.disabled = false;
        }
      }
    } else {
      // Focus on the first error field
      const firstErrorField = document.querySelector(".error-input");
      if (firstErrorField) {
        firstErrorField.focus();
      }
    }
  }

  async checkDuplicateEmailInCart(email) {
    try {
      // Get the current product's variant ID
      const variantIdInput = this.form.querySelector('[name="id"]');
      const currentVariantId = variantIdInput ? parseInt(variantIdInput.value) : null;

      // Fetch current cart
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Check if any items in the cart have the same email AND same variant ID
      // This allows the same email across different courses, but not for the same course
      const duplicateFound = cart.items.some(item => {
        // Check if this item has properties
        if (item.properties && typeof item.properties === 'object') {
          // Look for an Email property in the item properties
          const itemEmail = item.properties.Email || item.properties.email;

          // Check if both email AND variant ID match
          if (itemEmail &&
              itemEmail.toLowerCase() === email.toLowerCase() &&
              item.variant_id === currentVariantId) {
            return true;
          }
        }
        return false;
      });

      return duplicateFound;
    } catch (error) {
      console.error('Error checking cart for duplicate email:', error);
      // If there's an error fetching the cart, allow the submission to proceed
      return false;
    }
  }

  isValidEmail(email) {
    // Basic email validation regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  isValidPhone(phone) {
    // Check if the phone contains only numbers, spaces, dashes, parentheses, or plus signs
    const phoneRegex = /^[0-9\s\-\(\)\+]+$/;
    return phoneRegex.test(phone);
  }

  showError(field, message) {
    // For select fields, we need to target the parent select-wrapper
    const targetElement =
      field.tagName === "SELECT" ? field.closest(".select-wrapper") : field;

    // Add error class to input
    field.classList.add("error-input");

    // Create and show error message
    const errorMessage = document.createElement("div");
    errorMessage.classList.add("course-field-error");
    errorMessage.textContent = message;

    targetElement.parentNode.appendChild(errorMessage);
  }

  resetErrors() {
    // Remove all error messages
    const errorMessages = document.querySelectorAll(".course-field-error");
    errorMessages.forEach((msg) => msg.remove());

    // Remove error class from inputs
    const errorInputs = document.querySelectorAll(".error-input");
    errorInputs.forEach((input) => input.classList.remove("error-input"));
  }
}

customElements.define(
  "course-information-validation",
  CourseInformationValidation
);
